import {
    Button,
    Card,
    CardList,
    DarkModeToggle,
    documentObject,
    Flexer,
    FOLD_DRAG_LOCK,
    Header,
    Heading,
    Icon,
    If,
    Image,
    Option,
    Options,
    Pill,
    Progress,
    Stack,
    Text,
    useDrag,
    useStorage,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useState } from 'react'
import { TbFilters, TbHeart } from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'
import { Block, Error, ProjectUserPreview, Tasks, TeamUserPreview } from '../components'
import { byPosition, logger, useDocumentTitle } from '../helpers'
import { useProjects, useTasks } from '../hooks/api.hooks'
import { LogoNew } from '../logo'
import { FRONTEER_JWT, useAuth } from '../hooks/auth.hook'
import { useAppStore } from '../store/app'
import { useSubscription } from '../hooks/websocket.hook'

const Disclaimer = () => {
    return (
        <Text
            textAlign="center"
            colorToken="indigo-50">
            By using Fronteer, you agree to our{' '}
            <a
                href="https://fronteer.app/terms-of-use"
                target="_blank"
                style={{ color: 'currentColor' }}>
                terms of use
            </a>{' '}
            and{' '}
            <a
                href="https://fronteer.app/license"
                target="_blank"
                style={{ color: 'currentColor' }}>
                license agreement
            </a>
            .
        </Text>
    )
}

export default (props) => {
    const projectsAPI = useProjects()
    const [error, setError] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [joined, setJoined] = useState(false)
    const [project, setProject] = useState(null)
    const [tasks, setTasks] = useState([])
    const actions = useAppStore((state) => state.actions)
    const tasksAPI = useTasks()
    const { shortcode } = useParams()
    const { setStorage, getStorage } = useStorage()
    const { currentAuthenticatedUser, parseJwt } = useAuth()
    const [loggedIn, setLoggedIn] = useState(false)
    const [option, setOption] = useState(0)
    const view = useMemo(() => {
        switch (option) {
            case 0:
                return 'list'
            case 1:
                return 'board'
            case 2:
                return 'calendar'
        }
    }, [option])

    const handleProjectJoin = async () => {
        setLoading(true)
        setError(false)

        try {
            //await projectsAPI.join({ shortcode, id, workspaceId, teamId })

            setLoading(false)
            setJoined(true)
        } catch (e) {
            setLoading(false)
            setError('Could not join project, you might already be part of it.')
        }
    }

    const fetchData = async () => {
        setLoading(true)
        setError(null)

        try {
            const token = getStorage(FRONTEER_JWT)
            const project: any = await projectsAPI.roadmap({ shortcode })

            setLoggedIn(!!token)

            if (!project) throw Error

            // use the current token if hasn't expired
            if (!token) {
                setStorage(FRONTEER_JWT, project.token)
            } else {
                const { exp } = parseJwt(token)
                const now = new Date().getTime() / 1000
                if (exp < now) setStorage(FRONTEER_JWT, project.token)
            }

            const tasks = await tasksAPI.shortcode({ shortcode })

            actions.setViewType('project')

            setTasks(tasks)
            setProject(project)
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    useDocumentTitle(project?.name || 'Fronteer')

    useEffect(() => {
        if (!!shortcode) fetchData()
        documentObject.body.style.backgroundColor = 'var(--f-color-surface)'
    }, [shortcode])

    useEffect(() => {
        window[FOLD_DRAG_LOCK] = true
    })

    useSubscription(project?.id)

    if (error) return <Error error={error} />

    return (
        <View
            column
            alignItems="stretch"
            alignContent="stretch"
            justifyContent="stretch"
            bgToken="background"
            className="public"
            width="100%"
            height="100%"
            position="absolute"
            style={{ inset: 0 }}>
            <If if={loading}>
                <Progress
                    value={50}
                    thickness={5}
                    indeterminate
                    bgToken="transparent"
                    position="absolute"
                    variant="success"
                    style={{
                        '--f-progress-radius': 0,
                        'top': 0,
                        'left': 0,
                    }}
                />
            </If>

            <Header
                bg={project?.color || 'var(--f-color-gray-900)'}
                height={50}
                border="none"
                gap="0.5rem"
                p="0 0.5rem 0 1rem"
                style={{
                    '--f-color-surface': project?.color || 'var(--f-color-gray-900)',
                    '--f-color-surface-stronger': 'var(--f-color-surface-strongest)',
                }}>
                <Pill
                    size="sm"
                    color="#ffffff"
                    outline>
                    {project?.workspace?.name}
                </Pill>
                <Heading
                    as="h5"
                    color="white">
                    {project?.name}
                </Heading>
                <Flexer />
                {!!project?.team?.id && (
                    <TeamUserPreview
                        teamId={project?.team?.id}
                        workspaceId={project?.workspaceId}
                        size="sm"
                    />
                )}
                {!project?.team?.id && (
                    <ProjectUserPreview
                        projectId={project?.id}
                        workspaceId={project?.workspaceId}
                        size="sm"
                    />
                )}
                {project?.joinable && (
                    <Button
                        as="a"
                        target="_blank"
                        href={`${window.location.origin}/c/${shortcode}`}
                        height={32}
                        p="0 1rem"
                        colorToken="white"
                        bg="rgba(255,255,255,0.2)"
                        border="none">
                        Join project
                    </Button>
                )}
                <Button
                    as="a"
                    target="_blank"
                    href={`${window.location.origin}/c/${shortcode}`}
                    height={35}
                    p="0 1rem"
                    colorToken="white"
                    bg="rgba(255,255,255,0.2)"
                    border="none">
                    {loggedIn ? 'Go to Fronteer' : 'Log in / Sign up'}
                </Button>
            </Header>

            <Header height={50}>
                {/* <Text>
                    This is a public project and is in read-only mode.
                </Text>
                <Flexer /> */}
                <Options
                    selected={option}
                    onOptionChange={setOption}
                    m="0 auto">
                    <Option>List</Option>
                    <Option>Board</Option>
                    <Option>Calendar</Option>
                </Options>
            </Header>

            {/*             
            <View
                column
                gap="1rem"
                height="100%"
                p="2rem 2rem"
                alignItems="flex-start"
                alignContent="flex-start"
                justifyContent="flex-start"
                width={300}
                position="sticky"
                style={{ top: '2rem', borderRight: '1px solid var(--f-color-border)' }}>
                {!!project?.roadmapImage && (
                    <Image
                        src={project?.roadmapImage}
                        height={200}
                    />
                )}
                <View
                    row
                    width="100%">
                    <Pill outline>{project?.workspace?.name}</Pill>
                    <Flexer />
                    <DarkModeToggle />
                </View>
                <Heading as="h2">{project?.name}</Heading>
                {!!project?.roadmapDescription && (
                    <Text
                        size="sm"
                        colorToken="text-weaker"
                        className="roadmap__description"
                        dangerouslySetInnerHTML={{ __html: project?.roadmapDescription }}
                    />
                )}
                {project?.joinable && (
                    <Button
                        as="a"
                        target="_blank"
                        href={`${window.location.origin}/c/${shortcode}`}
                        size="lg"
                        width="100%"
                        variant="accent">
                        Join Now
                    </Button>
                )}
                <View
                    row
                    gap="1rem"
                    radius="var(--f-radius)"
                    p="0.75rem 1rem"
                    //bgToken="highlight-weak"
                    border="1px solid var(--f-color-indigo-500)">
                    <LogoNew
                        scale={0.25}
                        color="var(--f-color-indigo-500)"
                    />
                    <Text
                        size="sm"
                        colorToken="indigo-500">
                        This public roadmap is powered by Fronteer, currently in Early Access.
                    </Text>
                    
                </View>
            </View> 
            */}

            <View
                height="calc(100% - 100px)"
                position="relative"
                flex={1}>
                {!!project?.id && (
                    <>
                        <Tasks
                            view={view}
                            workspaceId={project.workspace.id}
                            projectId={project.id}
                            teamId={project.teamId}
                            type="project"
                        />
                    </>
                )}
            </View>
        </View>
    )
}
